/**
 * ChatMarkdown
 *
 * Shared markdown renderer for chat-style bubbles across the app
 * (public agent chat, internal agent chat, dashboard ChatAssistant).
 *
 * Why this lives here:
 *   - Before #2404 the dashboard ChatAssistant used an artisanal regex
 *     renderer that only handled **bold**, *italic*, `code` and bullets.
 *     LLMs that emit `#`/`##`/`###` headings, `>` quotes, links, tables
 *     or `---` rules had those tokens leaked through as raw `#` text,
 *     which users described as "umas # umas coisas escrotas".
 *   - The public agent bubble already used react-markdown but was missing
 *     the H1..H6 mappings, so headings rendered with the browser default
 *     (h1 ≈ 32px) and overflowed inside the bubble.
 *
 * This component delegates to react-markdown + remark-gfm and ships
 * bubble-friendly styling: tight headings, block code with language
 * label, inline code pill, blockquote, table, hr.
 *
 * Headings keep semantic <h1>..<h6> so screen readers still announce
 * the hierarchy; only the *visual* size is tuned down to bubble scale.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const HEADING_STYLES = {
  h1: { fontSize: '1.05rem', fontWeight: 700, mt: 1, mb: 0.5 },
  h2: { fontSize: '1rem', fontWeight: 700, mt: 1, mb: 0.5 },
  h3: { fontSize: '0.95rem', fontWeight: 600, mt: 0.75, mb: 0.5 },
  h4: { fontSize: '0.9rem', fontWeight: 600, mt: 0.75, mb: 0.5 },
  h5: { fontSize: '0.88rem', fontWeight: 600, mt: 0.5, mb: 0.25 },
  h6: { fontSize: '0.85rem', fontWeight: 600, mt: 0.5, mb: 0.25 },
};

const makeHeadingRenderer = (tag) => ({ node, ...props }) => (
  <Typography
    component={tag}
    sx={{
      ...HEADING_STYLES[tag],
      lineHeight: 1.3,
      '&:first-of-type': { mt: 0 },
    }}
    {...props}
  />
);

const COMPONENTS = {
  p: ({ node, ...props }) => (
    <Typography
      variant="body2"
      component="p"
      sx={{ m: 0, mb: 0.75, '&:last-child': { mb: 0 }, lineHeight: 1.55 }}
      {...props}
    />
  ),
  h1: makeHeadingRenderer('h1'),
  h2: makeHeadingRenderer('h2'),
  h3: makeHeadingRenderer('h3'),
  h4: makeHeadingRenderer('h4'),
  h5: makeHeadingRenderer('h5'),
  h6: makeHeadingRenderer('h6'),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: 0, borderTop: '1px solid', borderColor: 'divider', my: 1 }}
    />
  ),
  strong: ({ node, ...props }) => (
    <strong style={{ fontWeight: 600 }} {...props} />
  ),
  em: ({ node, ...props }) => <em {...props} />,
  // Children come from react-markdown, but the linter can't statically
  // verify that — silence jsx-a11y/anchor-has-content here.
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  a: ({ node, children, ...props }) => (
    <Box
      component="a"
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: 'primary.main', wordBreak: 'break-word' }}
    >
      {children}
    </Box>
  ),
  ul: ({ node, ...props }) => (
    <ul style={{ margin: '4px 0 8px', paddingLeft: 22 }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol style={{ margin: '4px 0 8px', paddingLeft: 22 }} {...props} />
  ),
  li: ({ node, ...props }) => (
    <li style={{ marginBottom: 2, lineHeight: 1.55 }} {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: '3px solid',
        borderColor: 'divider',
        pl: 1.5,
        ml: 0,
        my: 0.75,
        color: 'text.secondary',
        fontStyle: 'italic',
      }}
      {...props}
    />
  ),
  // Inline code keeps a pill style; block code uses a scrollable panel
  // with an optional language tag header.
  code: ({ inline, className, children, node, ...props }) => {
    const text = String(children).replace(/\n$/, '');
    if (inline) {
      return (
        <Typography
          component="code"
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.text.primary, 0.1),
            color: 'text.primary',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.85em',
          })}
          {...props}
        >
          {children}
        </Typography>
      );
    }
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    return (
      <Box sx={{ my: 0.75, borderRadius: 1.5, overflow: 'hidden' }}>
        {language !== 'text' && (
          <Typography
            component="div"
            variant="caption"
            sx={(theme) => ({
              display: 'block',
              px: 1.5,
              py: 0.5,
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'lowercase',
            })}
          >
            {language}
          </Typography>
        )}
        <Box
          component="pre"
          sx={(theme) => ({
            m: 0,
            p: 1.5,
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            color: 'text.primary',
            fontSize: '0.82em',
            lineHeight: 1.5,
            overflow: 'auto',
            maxWidth: '100%',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, "Roboto Mono", monospace',
          })}
        >
          <Box component="code" sx={{ display: 'block', whiteSpace: 'pre' }}>
            {text}
          </Box>
        </Box>
      </Box>
    );
  },
  table: ({ node, ...props }) => (
    <Box
      component="table"
      sx={(theme) => ({
        borderCollapse: 'collapse',
        my: 0.75,
        fontSize: '0.85em',
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          padding: '4px 8px',
          textAlign: 'left',
        },
        '& th': { bgcolor: alpha(theme.palette.text.primary, 0.06), fontWeight: 600 },
      })}
      {...props}
    />
  ),
};

const ChatMarkdown = ({ content, sx }) => (
  <Box
    sx={{
      '& > *:first-of-type': { mt: 0 },
      '& > *:last-child': { mb: 0 },
      ...sx,
    }}
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
      {content || ''}
    </ReactMarkdown>
  </Box>
);

ChatMarkdown.propTypes = {
  content: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types -- forwarded to MUI sx
  sx: PropTypes.object,
};

ChatMarkdown.defaultProps = {
  content: '',
  sx: undefined,
};

export default ChatMarkdown;
